import { createReducer, on } from "@ngrx/store";
import { AppState, initialState } from "./state";
import * as appActions from "./actions";
import * as programActions from "../program/actions";
import { AppRoutes, CardType, WalletAppleLayout, WalletPlatform, WalletSideVisible } from "../../enums";
import { jwtDecode } from "jwt-decode";
import { routerNavigatedAction } from "@ngrx/router-store";

export const appReducer = createReducer(
  initialState,

  on(appActions.setAccessToken, (state, { accessToken }): AppState => {
    let userId: string;

    try {
      const payload = jwtDecode(accessToken);
      userId = payload.sub;
    } catch {}

    return { ...state, accessToken, userId };
  }),

  on(appActions.setIframeHost, (state, { iframeHost }): AppState => ({ ...state, iframeHost })),

  on(appActions.setIframeShop, (state, { iframeShop }): AppState => ({ ...state, iframeShop })),

  on(appActions.setIframeProgramId, (state, { iframeProgramId }): AppState => ({ ...state, iframeProgramId })),

  on(appActions.setIframeProgramName, (state, { iframeProgramName }): AppState => ({ ...state, iframeProgramName })),

  on(appActions.setIframeIntegrationName, (state, { iframeIntegrationName }): AppState => ({ ...state, iframeIntegrationName })),

  on(appActions.logOut, (state): AppState => ({ ...state, accessToken: null })),

  on(appActions.setDirtyForm, (state, { dirtyForm }): AppState => ({ ...state, dirtyForm })),

  // These leave the editor on purpose (a new entity opens on its own route, a deleted one goes back to its list),
  // sometimes before the editor's last write marks it saved: the leave guard must not ask about them
  on(
    programActions.createCampaignSuccess,
    programActions.deleteCampaignSuccess,
    programActions.createBoostSuccess,
    programActions.archiveBoostSuccess,
    programActions.createRewardSuccess,
    programActions.deleteRewardSuccess,
    programActions.createEngagementSuccess,
    programActions.deleteEngagementSuccess,
    programActions.deleteLocationSuccess,
    programActions.generateApiKeySuccess,
    (state): AppState => ({ ...state, dirtyForm: false }),
  ),

  on(appActions.setUploadImageLoading, (state, { uploadImageLoading }): AppState => ({ ...state, uploadImageLoading })),

  on(appActions.changeLanguageSuccess, (state, { language }): AppState => ({ ...state, language })),

  on(
    appActions.setAnalyticsFilters,
    (state, { type, ...analyticsFilters }): AppState => ({
      ...state,
      analyticsFilters: {
        ...state.analyticsFilters,
        ...analyticsFilters,
      },
    }),
  ),

  on(appActions.setDesignWalletPlatform, (state, { wallet }) => ({
    ...state,
    pages: {
      ...state.pages,
      [AppRoutes.design]: {
        ...state.pages[AppRoutes.design],
        walletPlatform:
          wallet || (state.pages[AppRoutes.design].walletPlatform === WalletPlatform.Apple ? WalletPlatform.Google : WalletPlatform.Apple),
      },
    },
  })),

  on(appActions.setDesignWalletSideVisible, (state, { side }) => ({
    ...state,
    pages: {
      ...state.pages,
      [AppRoutes.design]: {
        ...state.pages[AppRoutes.design],
        walletSideVisible:
          side ||
          (state.pages[AppRoutes.design].walletSideVisible === WalletSideVisible.Front ? WalletSideVisible.Back : WalletSideVisible.Front),
      },
    },
  })),

  on(appActions.setDesignWalletAppleLayout, (state, { layout }) => ({
    ...state,
    pages: {
      ...state.pages,
      [AppRoutes.design]: {
        ...state.pages[AppRoutes.design],
        walletAppleLayout:
          layout ||
          (state.pages[AppRoutes.design].walletAppleLayout === WalletAppleLayout.Poster ? WalletAppleLayout.Classic : WalletAppleLayout.Poster),
      },
    },
  })),

  on(appActions.setScheduledWalletPlatform, (state, { wallet }) => ({
    ...state,
    pages: {
      ...state.pages,
      [AppRoutes.scheduled]: {
        ...state.pages[AppRoutes.scheduled],
        walletPlatform:
          wallet ||
          (state.pages[AppRoutes.scheduled].walletPlatform === WalletPlatform.Apple ? WalletPlatform.Google : WalletPlatform.Apple),
      },
    },
  })),

  on(appActions.setLocationBasedWalletPlatform, (state, { wallet }) => ({
    ...state,
    pages: {
      ...state.pages,
      [AppRoutes.locationBased]: {
        ...state.pages[AppRoutes.locationBased],
        walletPlatform:
          wallet ||
          (state.pages[AppRoutes.locationBased].walletPlatform === WalletPlatform.Apple ? WalletPlatform.Google : WalletPlatform.Apple),
      },
    },
  })),

  on(appActions.setTriggeredBasedWalletPlatform, (state, { wallet }) => ({
    ...state,
    pages: {
      ...state.pages,
      [AppRoutes.coupons]: {
        ...state.pages[AppRoutes.coupons],
        walletPlatform:
          wallet ||
          (state.pages[AppRoutes.coupons].walletPlatform === WalletPlatform.Apple ? WalletPlatform.Google : WalletPlatform.Apple),
      },
    },
  })),

  on(appActions.setAnonymousWalletPlatform, (state, { wallet }) => ({
    ...state,
    pages: {
      ...state.pages,
      [AppRoutes.anonymous]: {
        ...state.pages[AppRoutes.anonymous],
        walletPlatform:
          wallet ||
          (state.pages[AppRoutes.anonymous].walletPlatform === WalletPlatform.Apple ? WalletPlatform.Google : WalletPlatform.Apple),
      },
    },
  })),

  on(appActions.setTriggeredBasedType, (state, { cardType }) => ({
    ...state,
    pages: {
      ...state.pages,
      [AppRoutes.coupons]: {
        ...state.pages[AppRoutes.coupons],
        type: cardType || (state.pages[AppRoutes.coupons].type === CardType.notification ? CardType.wallet : CardType.notification),
      },
    },
  })),

  on(appActions.toggleSideNav, (state, { open }) => ({
    ...state,
    sideNav: {
      ...state.sideNav,
      open: open !== undefined ? open : !state.sideNav.open,
    },
  })),

  on(appActions.mobileSideNav, (state, { isMobile: mobile }) => ({
    ...state,
    sideNav: {
      ...state.sideNav,
      mobile,
    },
  })),

  on(appActions.collapseSideNav, (state, { collapsed }) => ({
    ...state,
    sideNav: {
      ...state.sideNav,
      collapsed,
    },
  })),

  on(appActions.setSideNav, (state, { active, hasLogOut }) => ({
    ...state,
    sideNav: {
      ...state.sideNav,
      active,
      hasLogOut,
    },
  })),

  on(appActions.setOnboardingPending, (state, { pending }): AppState => ({ ...state, onboardingPending: pending })),

  on(appActions.toggleHomeStep, (state, { stepKey, completed }): AppState => ({
    ...state,
    homeCompletedSteps: { ...state.homeCompletedSteps, [stepKey]: completed },
  })),

  on(appActions.dismissHomeGuide, (state, { guide }): AppState => ({
    ...state,
    homeDismissedGuides: { ...state.homeDismissedGuides, [guide]: true },
  })),

  // A reinstall is a new install: ticked steps, dismissed guides and the last answers about the old one no longer apply
  on(appActions.syncHomeInstall, (state, { key }): AppState =>
    !state.homeInstallKey || state.homeInstallKey === key
      ? { ...state, homeInstallKey: key }
      : {
          ...state,
          homeInstallKey: key,
          homeCompletedSteps: {},
          homeDismissedGuides: {},
          homeExtensionHandles: null,
          homeSummary: null,
        },
  ),

  on(appActions.setHomeExtensionHandles, (state, { handles }): AppState => ({ ...state, homeExtensionHandles: handles })),
  on(appActions.setHomeSummary, (state, { summary }): AppState => ({ ...state, homeSummary: summary })),
  // A shop without a program starts over: nothing the last visit left describes it
  // A reinstall starts over: Home's guide, the last page and the onboarding belonged to the previous install
  on(programActions.programNotFound, (state): AppState => ({
    ...state,
    homeSummary: null,
    homeExtensionHandles: null,
    homeCompletedSteps: initialState.homeCompletedSteps,
    homeDismissedGuides: initialState.homeDismissedGuides,
    homeDismissedNotices: initialState.homeDismissedNotices,
    homeInstallKey: null,
    lastRoute: null,
    onboardingPending: false,
  })),

  on(appActions.dismissHomeNotice, (state, { kind, level }): AppState => ({
    ...state,
    homeDismissedNotices: { ...state.homeDismissedNotices, [kind]: Math.max(level, state.homeDismissedNotices[kind] ?? 0) },
  })),

  on(appActions.setServerDown, (state, { serverDown }): AppState => ({ ...state, serverDown })),

  on(appActions.setLastRoute, (state, { url }): AppState => ({ ...state, lastRoute: url })),

  on(appActions.showPageError, (state, { pageError }): AppState => ({ ...state, pageError })),

  on(appActions.dismissPageError, routerNavigatedAction, (state): AppState => ({ ...state, pageError: null })),

  // The page error banner titles the program's formError by what failed
  on(
    programActions.updateProgramFailure,
    programActions.updateProgramTiersFailure,
    programActions.updateProgramTierFailure,
    programActions.disableProgramTiersFailure,
    programActions.persistProgramFailure,
    programActions.addLocationFailure,
    programActions.updateLocationFailure,
    programActions.createCampaignFailure,
    programActions.updateCampaignFailure,
    programActions.createBoostFailure,
    programActions.updateBoostFailure,
    programActions.addProgramIntegrationFailure,
    programActions.updateProgramIntegrationFailure,
    programActions.generateCouponCodeBatchFailure,
    programActions.createRewardFailure,
    programActions.updateRewardFailure,
    programActions.addDomainFailure,
    programActions.editDomainFailure,
    programActions.createEngagementFailure,
    programActions.updateEngagementFailure,
    programActions.upsertSectionTranslationsFailure,
    programActions.upsertEarningFlowTranslationsFailure,
    programActions.upsertRewardTranslationsFailure,
    programActions.upsertTierTranslationsFailure,
    programActions.upsertCouponTranslationsFailure,
    programActions.upsertCampaignTranslationsFailure,
    programActions.upsertProgramTranslationsFailure,
    programActions.generateApiKeyFailure,
    programActions.updateApiKeyFailure,
    (state): AppState => ({ ...state, formErrorTitle: "PAGE_ERROR.saveTitle" }),
  ),

  on(
    programActions.deleteLocationFailure,
    programActions.removeUnclaimedFailure,
    programActions.removeCouponBatchFailure,
    programActions.deleteCampaignFailure,
    programActions.deleteRewardFailure,
    programActions.deleteDomainFailure,
    programActions.deleteEngagementFailure,
    programActions.deleteDeviceFailure,
    programActions.deleteApiKeyFailure,
    programActions.revokeMcpConnectionFailure,
    programActions.deactivateMcpFailure,
    (state): AppState => ({ ...state, formErrorTitle: "PAGE_ERROR.deleteTitle" }),
  ),

  on(programActions.getApiKeyFailure, (state): AppState => ({ ...state, formErrorTitle: "PAGE_ERROR.loadTitle" })),

  on(
    programActions.resetFormError,
    programActions.addPointsToCustomerFailure,
    programActions.removePointsToCustomerFailure,
    programActions.sendPushFailure,
    programActions.archiveBoostFailure,
    programActions.createProgramSubscriptionFailure,
    (state): AppState => ({ ...state, formErrorTitle: null }),
  ),
);
